import React from 'react';
import {priority} from '@priority-software/priority-proto-api/lib';
import {isDate} from '../../utils/date';
import {isTime} from '../../utils/time';
import {DateInput} from './DateInput/DateInput';
import {TimeSumInput} from './TimeSumInput';
import {TextInput} from './TextInput';
import {NumberInput} from './NumberInput';
import {Column} from '../../../../stores/Form/Column';
import {OperatorInput} from './OperatorInput';
import {isOperatorField} from '../../utils/filters';
import {DayInput} from './DayInput';

export interface StringInputProps {
    column: Column;
    searchMode?: boolean;
    value: string;
    inputRef: React.RefObject<HTMLInputElement>;
    handleChange: (any) => void;
    handleKeyDown: (KeyboardEvent) => void;
    isReadonly?: boolean;
    isActive?: boolean;
    handleMouseDown: (e) => void;
    onFocus: (e) => void;
    onBlur?: () => void;
}

export type Props = {
    column: Column;
    searchMode?: boolean;
    value: string;
    inputRef: React.RefObject<HTMLInputElement>;
    onChange: (any) => void;
    onKeyDown: (KeyboardEvent) => void;
    isReadonly?: boolean;
    isActive?: boolean;
    onFocus: (e) => void;
    setMaxWidthMode: (arg: boolean) => void;
    setEditMode: (arg: boolean) => void;
    rowIndex: number;
    onBlur: () => void;
};

export const StringInput = ({
    column,
    searchMode,
    value = '',
    inputRef,
    setMaxWidthMode,
    setEditMode,
    onChange,
    onKeyDown,
    isReadonly,
    rowIndex,
    onFocus,
    onBlur
}: Props) => {
    const columnType = column.Type;

    const handleMouseDown = () => {
        if (setMaxWidthMode && !column.maxWidthMode && value?.length) {
            setMaxWidthMode(true);
        }
        if (value?.length) {
            setEditMode(true);
        }
    };

    const handleChange = (newValue: string) => {
        if (setMaxWidthMode && newValue.length && !column.maxWidthMode) {
            setMaxWidthMode(true);
        }
        onChange(newValue === ' ' ? value : newValue);
    };

    if (isOperatorField(searchMode, rowIndex)) {
        return (
            <OperatorInput
                column={column}
                value={value}
                handleMouseDown={handleMouseDown}
                handleChange={handleChange}
                handleKeyDown={onKeyDown}
                inputRef={inputRef}
                onFocus={onFocus}
            />
        );
    }
    if (isDate(columnType) || isTime(columnType)) {
        return (
            <DateInput
                column={column}
                value={value}
                handleMouseDown={handleMouseDown}
                handleChange={handleChange}
                handleKeyDown={onKeyDown}
                inputRef={inputRef}
                searchMode={searchMode}
                isReadonly={isReadonly}
                onFocus={onFocus}
                onBlur={onBlur}
            />
        );
    } else if (columnType === priority.netitems.form.ColumnType.Time6) {
        return (
            <TimeSumInput
                column={column}
                value={value}
                handleMouseDown={handleMouseDown}
                handleChange={handleChange}
                handleKeyDown={onKeyDown}
                inputRef={inputRef}
                searchMode={searchMode}
                isReadonly={isReadonly}
                onFocus={onFocus}
                onBlur={onBlur}
            />
        );
    } else if (columnType === priority.netitems.form.ColumnType.Day) {
        return (
            <DayInput
                column={column}
                value={value}
                handleMouseDown={handleMouseDown}
                handleChange={handleChange}
                handleKeyDown={onKeyDown}
                inputRef={inputRef}
                searchMode={searchMode}
                isReadonly={isReadonly}
                onFocus={onFocus}
                onBlur={onBlur}
            />
        );
    } else if (
        columnType === priority.netitems.form.ColumnType.Real ||
        columnType === priority.netitems.form.ColumnType.Int ||
        columnType === priority.netitems.form.ColumnType.Unsigned
    ) {
        return (
            <NumberInput
                column={column}
                value={value}
                handleMouseDown={handleMouseDown}
                handleChange={handleChange}
                handleKeyDown={onKeyDown}
                inputRef={inputRef}
                searchMode={searchMode}
                isReadonly={isReadonly}
                onFocus={onFocus}
                isPositive={columnType === priority.netitems.form.ColumnType.Unsigned}
                onBlur={onBlur}
            />
        );
    }

    return (
        <TextInput
            column={column}
            value={value}
            handleMouseDown={handleMouseDown}
            handleChange={handleChange}
            handleKeyDown={onKeyDown}
            inputRef={inputRef}
            isReadonly={isReadonly}
            onFocus={onFocus}
            onBlur={onBlur}
        />
    );
};
